import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { INotificationItem, NotificationType } from '@src/types/notifications.types'
import { INotificationV2User, IUser } from '@src/types/user.types'
import { IPaginationLinks } from '@src/types/api.types'
import _orderBy from 'lodash/orderBy'
import notificationsApi from '@src/api/notifications'
import _concat from 'lodash/concat'
import _uniqBy from 'lodash/uniqBy'
import { notificationsV2Api } from '@src/api/apiModuleInstances'
import { getWindowObject } from '@src/utilities/iframeDetection'
import _find from 'lodash/find'
import { getApiTokenAsPromise } from '@src/helpers/cookie'

export const useNotificationStore = defineStore('notifications', () => {
  const notifications = ref<INotificationItem<IUser | INotificationV2User>[]>([])
  const unseenNotificationsCount = ref(0)
  const links = ref<IPaginationLinks | undefined>(
    { self: { href: '' }, next: { href: 'offset=0' } }
  )
  const drawerVisible = ref(false)
  const forcedBadgeNotification = ref<INotificationItem<IUser | INotificationV2User> | null>(null)
  const loading = ref(true)
  const loadingNewNotifications = ref(false)
  const isFirstLoad = ref(true)

  const sortedNotifications = computed(() => {
    return _orderBy(notifications.value, [ 'is_read', 'created', 'id' ], [ 'asc', 'desc', 'desc' ])
  })

  const offset = computed(() => {
    const regex = /offset=(\d+)/g
    const nextHref = links.value?.next?.href
    return nextHref ? regex.exec(nextHref)?.[1] : undefined
  })

  const notificationsCountAsString = computed(() => {
    return unseenNotificationsCount.value > 99
      ? '99+' : unseenNotificationsCount.value?.toString()
  })

  const unreadBadgesNotifications = computed(() => {
    return notifications.value.filter((notification) => {
      return !notification.is_read
          && notification.type === NotificationType.NewBadgeObtained
    })
  })

  function setNotifications(newNotifications: INotificationItem[]) {
    notifications.value = _uniqBy([ ...notifications.value, ...newNotifications ], 'id')
  }

  function getV2Notifications(limit?: number) {
    try {
      return notificationsV2Api.getList(false, 0, limit)
    } catch (error) {
      return { data: { items: [] } }
    }
  }
  async function loadNotifications() {
    if (offset.value !== undefined) {
      try {
        loading.value = true
        const { data } = await notificationsApi.getList(false, parseInt(offset.value, 10))
        if (isFirstLoad.value) {
          const { data: v2data } = await getV2Notifications()
          setNotifications(_concat(data.items, v2data.items))
        } else {
          setNotifications(data.items)
        }
        links.value = data._links
      } finally {
        loading.value = false
      }
    }
  }

  function setNewNotifications(newnotifications: INotificationItem[]) {
    const notificationsIds = notifications.value.map((notification) => notification.id)
    const newFilteredNotifications = newnotifications.filter((notification) => !notificationsIds.includes(notification.id))
    notifications.value = [ ...newFilteredNotifications, ...notifications.value ]
  }

  async function addNewNotifications({ limitV1, limitV2 }: { limitV1: number, limitV2: number }) {
    loadingNewNotifications.value = true
    try {
      const res = await Promise.all([
        limitV1 ? notificationsApi.getList(false, 0, limitV1) : Promise.resolve({ data: { items: [] } }),
        limitV2 ? getV2Notifications(limitV2) : Promise.resolve({ data: { items: [] } })
      ])

      const newItems = res.reduce((acc, currentValue) => {
        return _concat(currentValue.data.items, acc)
      }, [] as INotificationItem[])

      setNewNotifications(newItems)
    } finally {
      loadingNewNotifications.value = false
    }
  }

  function getCleanPageTitle() {
    return getWindowObject().document.title.replace(/\(\d+\)/, '').trim()
  }

  function setUnseenNotificationsCount(count: number) {
    unseenNotificationsCount.value = count
    const pageTitle = getCleanPageTitle()
    getWindowObject().document.title = count ? `(${count}) ${pageTitle}` : pageTitle
  }

  async function markAllAsSeen() {
    await Promise.all([ notificationsApi.markAllAsSeen(), notificationsV2Api.markAllAsSeen() ])
    setUnseenNotificationsCount(0)
  }

  async function loadUnseenNotificationsCount() {
    try {
      const responses = await Promise.all([
        notificationsApi.getUnseenCount(),
        (() => {
          try {
            return notificationsV2Api.getUnseenCount()
          } catch (error) {
            return { data: { notifications: 0 } }
          }
        })()
      ])

      const v1Unseen = responses[0].data.notifications
      const v2Unseen = responses[1].data.notifications
      const unseenSum = v1Unseen + v2Unseen

      // if the number of new notifications is not equal to the current set new notifications
      if (unseenSum !== unseenNotificationsCount.value) {
        setUnseenNotificationsCount(unseenSum)
        // load new notifications in also for the case when the drawer is open
        if (!isFirstLoad.value) {
          if (unseenNotificationsCount.value) {
            addNewNotifications({ limitV1: v1Unseen, limitV2: v2Unseen })
          }
          if (drawerVisible.value) {
            markAllAsSeen()
          }
        }
      }
    } catch {
      //
    }
  }

  function markNotificationAsReadInternally(id: number) {
    const notification = _find(notifications.value, { id })
    if (notification) {
      notification.is_read = true
    }
  }

  async function markNotificationAsRead(id: number) {
    const notification = notifications.value.find((n) => n.id === id)
    if (notification && notification.v2) {
      await notificationsV2Api.markAsRead(id)
    } else {
      await notificationsApi.markAsRead(id)
    }
    markNotificationAsReadInternally(id)
  }

  function markAllNotificationsAsRead() {
    notifications.value = notifications.value.map((notification) => {
      return {
        ...notification,
        is_read: true
      }
    })
  }
  async function markAllAsRead() {
    await Promise.all([ notificationsApi.markAllAsRead(), notificationsV2Api.markAllAsRead() ])
    markAllNotificationsAsRead()
    drawerVisible.value = false
  }

  async function loadInitialNotifications() {
    try {
      if (isFirstLoad.value) {
        await getApiTokenAsPromise()
        await loadNotifications()
        isFirstLoad.value = false
        await loadUnseenNotificationsCount()
      }
    } catch {
      //
    }
  }

  async function showDrawer() {
    drawerVisible.value = true
    await loadInitialNotifications()
    if (unseenNotificationsCount.value) {
      await markAllAsSeen()
    }
  }

  function markAllBadgeNotificationsAsRead() {
    notifications.value = notifications.value.map((notification) => {
      if (notification.type === NotificationType.NewBadgeObtained) {
        return {
          ...notification,
          is_read: true
        }
      }
      return notification
    })
  }

  async function markBadgesNotificationsAsRead() {
    forcedBadgeNotification.value = null
    if (unreadBadgesNotifications.value.length) {
      try {
        const markUnreadBadgesAsReadPromises = unreadBadgesNotifications.value.map((badgeNotification) => notificationsApi.markAsRead(badgeNotification.id))
        await Promise.all(markUnreadBadgesAsReadPromises)
        markAllBadgeNotificationsAsRead()
        loadUnseenNotificationsCount()
      } catch {
        //
      }
    }
  }

  function showBadgeModal(notification: INotificationItem<IUser | INotificationV2User>) {
    forcedBadgeNotification.value = notification
    drawerVisible.value = false
  }

  function incrementUnseenNotificationsCount() {
    unseenNotificationsCount.value += 1
    const pageTitle = getCleanPageTitle()
    getWindowObject().document.title = unseenNotificationsCount.value ? `(${unseenNotificationsCount.value}) ${pageTitle}` : pageTitle
  }

  return {
    links,
    offset,
    loading,
    isFirstLoad,
    drawerVisible,
    notifications,
    sortedNotifications,
    loadingNewNotifications,
    unseenNotificationsCount,
    unreadBadgesNotifications,
    forcedBadgeNotification,
    notificationsCountAsString,
    incrementUnseenNotificationsCount,
    markAllBadgeNotificationsAsRead,
    markBadgesNotificationsAsRead,
    showBadgeModal,
    loadUnseenNotificationsCount,
    setUnseenNotificationsCount,
    loadInitialNotifications,
    markNotificationAsRead,
    addNewNotifications,
    setNewNotifications,
    loadNotifications,
    markAllAsSeen,
    markAllAsRead,
    showDrawer
  }
})
